/**
 * CPV UI kit · design tokens — the ONE place the CPV screens' colours, radii, spacing and type sizes are set.
 *
 * Components read them as CSS variables (`--cu-*`, applied by <CpvScreen> / <DetailDrawer> through `tokenVars`) or,
 * for SVG and recharts, through `useCpvTokens()`. No component carries a colour literal of its own.
 *
 * Tone names are display only: a page maps a SERVER code (health, plan status, kind, severity) to a tone; the word
 * shown is always the server's.
 */

const LIGHT = {
  bg: '#F4F6F8',
  card: '#FFFFFF',
  border: '#E4E7EC',
  borderStrong: '#D0D5DD',
  text: '#101828',
  muted: '#667085',
  subtle: '#F2F4F7',          // meta chips, table head
  controlBand: '#FED7AA',     // limits bar: the spec band behind the control band
  hover: '#F9FAFB',
  brand: '#204448',           // sidebar teal (same as the theme's sidebar default)
  brandActive: '#2E6166',     // active sidebar row
  brandText: '#E6F0F0',
  primary: '#1F3B3E',         // dark primary button
  primaryHover: '#2A4D51',
  onPrimary: '#FFFFFF',        // text on the primary and danger buttons (11.98:1 on primary)
  onBrand: '#FFFFFF',          // text on the active rail row
  focus: '#2563EB',
  tones: {
    info: { fg: '#1D4ED8', bg: '#EFF6FF', border: '#BFDBFE' },
    ok: { fg: '#15803D', bg: '#F0FDF4', border: '#BBF7D0' },
    warning: { fg: '#C2410C', bg: '#FFF7ED', border: '#FED7AA' },
    error: { fg: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
    kpi: { fg: '#7C3AED', bg: '#F5F3FF', border: '#DDD6FE' },
    teal: { fg: '#0F766E', bg: '#F0FDFA', border: '#99F6E4' },
    neutral: { fg: '#475467', bg: '#F2F4F7', border: '#E4E7EC' },
    accent: { fg: '#A21CAF', bg: '#FDF4FF', border: '#F5D0FE' },
    caution: { fg: '#854D0E', bg: '#FEFCE8', border: '#FDE047' },   // alert-limit flag (yellow)
  },
  /** Fills of the KPI composition bars and meters, one per tone (validated adjacent pairs; a label always names each). */
  bar: { ok: '#16A34A', warning: '#F59E0B', error: '#DC2626', info: '#2563EB', accent: '#C026D3', kpi: '#7C3AED', teal: '#0D9488',
    caution: '#EAB308', neutral: '#667085', faint: '#D0D5DD', track: '#EAECF0' },
  shadow: 'rgba(16, 24, 40, 0.08)',
  chart: {
    value: '#2563EB',
    target: '#667085',
    control: '#F59E0B',
    action: '#DC2626',
    spec: '#7C3AED',
    band: '#2563EB',
    grid: '#EAECF0',
    axis: '#667085',
    rule: '#0D9488',          // run-rule hit ring (validated against value / control / action; ring + legend + label, never colour alone)
    centre: '#15803D',        // centre line
    fit: '#475467',           // fitted distribution curve
    tolerance: '#C026D3',     // CPV-Q6 S3: a tolerance interval's bounds (dashed, always labelled)
    excluded: '#98A2B3',      // a point excluded from the statistics (hollow)
    seriesA: '#2563EB',       // CUSUM C+ (validated pair with seriesB: CVD ΔE 31, light)
    seriesB: '#EA580C',       // CUSUM C−
    /** One-hue sequential ramp (light → dark = less → more), e.g. signal age bands. */
    seq: ['#BFDBFE', '#60A5FA', '#2563EB', '#1E40AF', '#172554'],
    /** CPV-Q6 S4: one colour per compared group (batch overlay, box by group), always with a legend and the group's name. */
    groups: ['#2563EB', '#EA580C', '#0D9488', '#7C3AED', '#CA8A04', '#DB2777', '#475467', '#65A30D'],
  },
};

const DARK = {
  bg: '#0B1220',
  card: '#111827',
  border: '#1F2937',
  borderStrong: '#374151',
  text: '#F3F4F6',
  muted: '#9CA3AF',
  subtle: '#1F2937',
  controlBand: '#7C2D12',
  hover: '#162032',
  brand: '#0F172A',
  brandActive: '#1E3A3D',
  brandText: '#E6F0F0',
  primary: '#2E6166',
  primaryHover: '#3A747A',
  onPrimary: '#FFFFFF',        // 6.96:1 on primary, 5.30:1 on primaryHover
  onBrand: '#FFFFFF',
  focus: '#60A5FA',
  tones: {
    info: { fg: '#93C5FD', bg: '#172554', border: '#1E3A8A' },
    ok: { fg: '#86EFAC', bg: '#052E16', border: '#14532D' },
    warning: { fg: '#FDBA74', bg: '#431407', border: '#7C2D12' },
    error: { fg: '#FCA5A5', bg: '#450A0A', border: '#7F1D1D' },
    kpi: { fg: '#C4B5FD', bg: '#2E1065', border: '#4C1D95' },
    teal: { fg: '#5EEAD4', bg: '#042F2E', border: '#134E4A' },
    neutral: { fg: '#D1D5DB', bg: '#1F2937', border: '#374151' },
    accent: { fg: '#F0ABFC', bg: '#4A044E', border: '#701A75' },
    caution: { fg: '#FDE047', bg: '#422006', border: '#713F12' },
  },
  bar: { ok: '#22C55E', warning: '#FBBF24', error: '#F87171', info: '#60A5FA', accent: '#E879F9', kpi: '#A78BFA', teal: '#2DD4BF',
    caution: '#FACC15', neutral: '#9CA3AF', faint: '#4B5563', track: '#1F2937' },
  shadow: 'rgba(0, 0, 0, 0.45)',
  chart: {
    value: '#60A5FA',
    target: '#9CA3AF',
    control: '#FBBF24',
    action: '#F87171',
    spec: '#C4B5FD',
    band: '#60A5FA',
    grid: '#1F2937',
    axis: '#9CA3AF',
    rule: '#14B8A6',
    centre: '#4ADE80',
    fit: '#D1D5DB',
    tolerance: '#E879F9',
    excluded: '#6B7280',
    seriesA: '#3B82F6',       // validated pair on the dark card (#111827)
    seriesB: '#EA580C',
    /** On the dark card the ramp runs dark → light (less → more). */
    seq: ['#1E3A8A', '#1D4ED8', '#3B82F6', '#93C5FD', '#DBEAFE'],
    groups: ['#60A5FA', '#FB923C', '#2DD4BF', '#A78BFA', '#FACC15', '#F472B6', '#D1D5DB', '#A3E635'],
  },
};

export const RADIUS = { card: 12, control: 8, pill: 999 };
export const SPACE = { xs: 4, sm: 8, md: 12, lg: 16, xl: 20, xxl: 24, hair: 2 };
/** Q4j · the parameter master's stage route: a step's least width (a name wraps past it, never cut) and its dot. */
export const ROUTE = { stepMin: 200, dot: 24 };
/** Words a code is made readable with (`words()`) that stay in capitals: the GMP / CPV abbreviations a code may carry. */
export const ACRONYMS = new Set(['QC', 'QA', 'CPP', 'CQA', 'IPC', 'KPI', 'IMR', 'OOS', 'OOT', 'BMR', 'CPV', 'USL', 'LSL', 'UCL', 'LCL', 'PPQ', 'API', 'EM', 'CAPA', 'SOP']);
export const FONT_FAMILY = '"Inter", "Roboto", "Helvetica Neue", Arial, sans-serif'; // the app theme's font (themes/antd-theme.js)
export const FONT = { title: 28, pageTitle: 20, section: 18, body: 14, small: 12, kpi: 22, kpiUnit: 13, meta: 13, lead: 16, subtitle: 17, figure: 20, figureLg: 22, close: 24, tick: 11 };
/** Header meta chip height (page header and dashboard). */
export const META_CHIP = { height: 24 };
/** KPI card geometry: round icon, composition bar, meter, the tone accent, the left tone lining and the narrowest card. */
export const KPI = { icon: 32, iconGlyph: 16, bar: 8, meter: 4, accent: 3, lining: 4, minCard: 180, swatch: 10 };
/** The "i" hint button's glyph sizes: icon only, and beside its word. */
export const INFO = { glyph: 16, glyphWithText: 14 };
/** CPV-Q6 S1 · "Compare with…": the parameter picker's least width. */
export const COMPARE = { pickMin: 260 };
/** CPV-Q5m multivariate page geometry: the parameter picker's least width, the batch-statistic and score-plot heights,
 * the room an x-axis batch label needs, the contribution bars' row height, label and value columns, and the gap between a
 * loading's point and its label. */
export const MULTIVARIATE = { pickMin: 360, choiceMin: 200, statHeight: 260, scoreHeight: 340, xLabelRoom: 72, barRow: 28,
  barLabel: 200, barValue: 72, labelGap: 6 };
/**
 * Chart geometry (dataviz mark specs): 2px data lines, markers r ≥ 4 with a 2px surface ring, hairline grid, 4px rounded
 * bar ends with a 2px surface gap, 10% area wash; the zone washes step one hue (C lightest → A darkest).
 */
export const CHART = {
  line: 2, refLine: 1.5, hair: 1, marker: 4, markerSignal: 5, ring: 2, hitRing: 8, gap: 2, barRadius: 4,
  wash: 0.1, zone: { C: 0.04, B: 0.08, A: 0.13 },
  dash: { control: '5 4', spec: '7 4', action: '3 3', target: '2 4', tolerance: '8 3 2 3' },
  height: 340, mrHeight: 150, histHeight: 300, boxHeight: 300, capRow: 48, capAxis: 36, brush: 28, axisWidth: 60, xAxisHeight: 44, edgeLabel: 64, barMax: 24, boxMax: 48, boxGap: 8, tipWidth: 220,
  boxWash: 0.2, boxWashHover: 0.3, attributeDecimals: 5, attributeAxisDecimals: 4, modalWidth: 720,
  barOpacity: 0.85, dimOpacity: 0.35, minTickGap: 16,
  capLabel: 48, capValue: 132, spreadLabel: 210, spreadValue: 96,
  margin: { top: 16, right: 8, bottom: 4, left: 4 },
};
/** `appFooter`: the app's fixed footer bar (MainLayout, drawn above antd's drawers), which a drawer ends above. */
export const LAYOUT = { pageGutter: 24, sidebarWidth: 176, topBar: 64, drawerWidth: 560, drawerWidthWide: "min(880px, 92vw)", popoverWidth: 420, appFooter: 26 };
/**
 * CPV-AI-01 assistant: the workspace's prompt column width, the prompt box's rows, the answer time the PRD targets (§9:
 * under 10 s, shown as progress while waiting), the progress tick, the history page and the drawer's width.
 */
export const ASSISTANT = { promptCol: 340, promptRows: 3, targetSeconds: 10, tickMs: 1000, historyPage: 10, drawerWidth: 640, threadMax: 960, icon: 14, iconSm: 12,
  /** the thread's narrowest width before it moves under the prompt column; the product / parameter pickers; the
   *  question bubble's widest share of the thread; the Fact / Hypothesis marker; the feedback comment box */
  threadMin: 560, pick: 180, pickWide: 260, questionShare: '80%', marker: 88, feedbackBox: 380, leading: 1.5 };
/** Input timing: a list's search box asks the server once the reader has paused typing this long (ms). */
export const INPUT = { searchDebounceMs: 350, lookupMax: 10 };

/** Server code → tone (display only; the label is the server's word). */
export const HEALTH_TONE = { ACTION_REQUIRED: 'error', WATCH: 'warning', HEALTHY: 'ok' };
export const KIND_TONE = { CQA: 'info', CPP: 'ok', KPI: 'kpi', IPC: 'teal' };
export const PLAN_TONE = { DRAFT: 'neutral', IN_REVIEW: 'info', APPROVED: 'ok', SUPERSEDED: 'neutral', RETIRED: 'neutral' };
export const SEVERITY_TONE = { CRITICAL: 'error', HIGH: 'error', MEDIUM: 'warning', LOW: 'info' };
/** CPV-G25 parameter master (display only): master status, domain, sheet section and version mapping. */
export const MASTER_TONE = { DRAFT: 'neutral', IN_REVIEW: 'info', EFFECTIVE: 'ok', SUPERSEDED: 'neutral' };
export const DOMAIN_TONE = { PROCESS: 'teal', RELEASE: 'info', MATERIAL: 'accent', YIELD_KPI: 'kpi', STABILITY: 'warning', UTILITY_EM: 'neutral' };
export const SECTION_TONE = { PRODUCTION: 'teal', QC: 'info', ENGINEERING: 'neutral' };
export const MAPPING_TONE = { SAME: 'neutral', CHANGED: 'warning', DROPPED: 'error', ADDED: 'ok' };
/** CPV-G25 data sheets (display only): the sheet's stage (lifecycle stage key, or APPROVED / VOIDED), the entry flags, the disposition. */
export const SHEET_STATUS_TONE = { issue: 'neutral', entry: 'info', verification: 'warning', qa_approval: 'kpi', APPROVED: 'ok', VOIDED: 'neutral' };
export const FLAG_TONE = { OOS: 'error', ACTION: 'warning', ALERT: 'caution', CONTROL: 'info', IN_RANGE: 'ok', NO_LIMIT: 'neutral' };
export const DISPOSITION_TONE = { RELEASED: 'ok', REJECTED: 'error' };
/** A sheet section's state as the lifecycle read states it (returned, verified, submitted, overdue, open) → its tab dot. */
export const SECTION_STATE_TONE = { RETURNED: 'warning', VERIFIED: 'ok', SUBMITTED: 'info', OVERDUE: 'error', OPEN: 'faint' };
/** Touch layout (the tablet-ready entry rows): the control height. */
export const TOUCH = { control: 48, iconButton: 36 };
/**
 * The data sheet's dense entry grid and its header strip: the row height, each column's width, the stage row, the
 * limits bar's width, the header strip's field width and the section status dot.
 */
export const SHEET = { row: 52, stageRow: 32, paramCol: 220, limitsCol: 175, valueCol: 170, occurredCol: 185, refCol: 150, stampsCol: 195,
  toolsCol: 116, bar: 64, barTrack: 6, field: 170, dot: 8, icon: 14, chip: 18 };
/** G-03 dashboards (display only): health incl. not evaluated, capability band, risk class, plan waiting state. */
export const DASH_HEALTH_TONE = { ...HEALTH_TONE, NOT_EVALUATED: 'faint' };
export const CAPABILITY_TONE = { BELOW_TARGET: 'error', MARGINAL: 'warning', CAPABLE: 'ok', NOT_COMPUTED: 'neutral', NOT_EVALUATED: 'faint' };
export const RISK_TONE = { HIGH: 'error', MEDIUM: 'warning', LOW: 'info', UNCLASSIFIED: 'faint' };
/**
 * Q4c: the risk classes are the server's data (code, name, rank, tone). A class names its tone as one of these token
 * keys (the server's RiskTone, drawn as `cu-tone--<key>`); a code with no class row falls back to RISK_TONE, then neutral.
 */
export const RISK_CLASS_TONES = ['ok', 'info', 'teal', 'caution', 'warning', 'error', 'kpi', 'accent', 'neutral'];
export const riskClassTone = (classes, code) => {
  const t = (classes || []).find((c) => c.code === code)?.tone;
  return RISK_CLASS_TONES.includes(t) ? t : (RISK_TONE[code] || 'neutral');
};
export const WAITING_TONE = { AWAITING_REVIEW: 'info', AWAITING_APPROVAL: 'kpi' };
/** Dashboard geometry: chart heights, the matrix cell, the drill drawer and the bar list's label column. */
export const DASH = { chart: 260, chartSm: 200, cell: 76, cellH: 40, labelCol: 220, valueCol: 56, barH: 18, rowGap: 10, drawer: 640, stripRow: 34, list: 360 };
/**
 * The portfolio (CPV home) geometry: the featured parameter's sparkline, a product row's released-batches columns,
 * the least room one x-axis label needs before the axis labels every other bucket, a tick character's width as a share
 * of the font size, the gap kept between two labels, a count pill's line height and the health column's least width.
 */
export const PORTFOLIO = { sparkW: 300, sparkH: 64, miniW: 112, miniH: 24, miniGap: 2, tickSpace: 44, charWidth: 0.6, tickGap: 8,
  countLine: 20, healthMin: 140 };
export const SIGNAL_STATUS_TONE = { OPEN: 'warning', ASSESSED: 'info', ACTIONED: 'accent', CLOSED: 'ok', NO_ACTION: 'neutral' };

export const paletteOf = (isDark) => (isDark ? DARK : LIGHT);

/** The tokens as CSS variables, for a wrapper's `style`. */
export function tokenVars(isDark) {
  const p = paletteOf(isDark);
  const vars = {
    '--cu-bg': p.bg, '--cu-card': p.card, '--cu-border': p.border, '--cu-border-strong': p.borderStrong,
    '--cu-text': p.text, '--cu-muted': p.muted, '--cu-subtle': p.subtle, '--cu-hover': p.hover,
    '--cu-brand': p.brand, '--cu-brand-active': p.brandActive, '--cu-brand-text': p.brandText,
    '--cu-primary': p.primary, '--cu-primary-hover': p.primaryHover, '--cu-on-primary': p.onPrimary, '--cu-on-brand': p.onBrand,
    '--cu-control-band': p.controlBand, '--cu-focus': p.focus,
    '--cu-route-step-min': `${ROUTE.stepMin}px`, '--cu-route-dot': `${ROUTE.dot}px`,
    '--cu-radius': `${RADIUS.card}px`, '--cu-radius-sm': `${RADIUS.control}px`, '--cu-radius-pill': `${RADIUS.pill}px`,
    '--cu-space-xs': `${SPACE.xs}px`, '--cu-space-sm': `${SPACE.sm}px`, '--cu-space-md': `${SPACE.md}px`,
    '--cu-space-lg': `${SPACE.lg}px`, '--cu-space-xl': `${SPACE.xl}px`, '--cu-space-xxl': `${SPACE.xxl}px`,
    '--cu-font-family': FONT_FAMILY, '--cu-font-title': `${FONT.title}px`, '--cu-font-section': `${FONT.section}px`, '--cu-font-body': `${FONT.body}px`,
    '--cu-font-small': `${FONT.small}px`, '--cu-font-meta': `${FONT.meta}px`, '--cu-font-lead': `${FONT.lead}px`,
    '--cu-font-subtitle': `${FONT.subtitle}px`, '--cu-font-figure': `${FONT.figure}px`, '--cu-font-figure-lg': `${FONT.figureLg}px`,
    '--cu-font-close': `${FONT.close}px`, '--cu-space-hair': `${SPACE.hair}px`, '--cu-font-kpi': `${FONT.kpi}px`, '--cu-font-page-title': `${FONT.pageTitle}px`,
    '--cu-gutter': `${LAYOUT.pageGutter}px`, '--cu-app-footer': `${LAYOUT.appFooter}px`, '--cu-pop-w': `${LAYOUT.popoverWidth}px`, '--cu-font-kpi-unit': `${FONT.kpiUnit}px`, '--cu-shadow': p.shadow,
    '--cu-kpi-icon': `${KPI.icon}px`, '--cu-kpi-bar': `${KPI.bar}px`, '--cu-kpi-meter': `${KPI.meter}px`, '--cu-kpi-accent': `${KPI.accent}px`, '--cu-kpi-lining': `${KPI.lining}px`,
    '--cu-dim': CHART.dimOpacity, '--cu-kpi-min': `${KPI.minCard}px`, '--cu-kpi-swatch': `${KPI.swatch}px`, '--cu-touch': `${TOUCH.control}px`, '--cu-icon-btn': `${TOUCH.iconButton}px`, '--cu-meta-h': `${META_CHIP.height}px`,
    '--cs-row': `${SHEET.row}px`, '--cs-stage-row': `${SHEET.stageRow}px`, '--cs-param-col': `${SHEET.paramCol}px`, '--cs-limits-col': `${SHEET.limitsCol}px`,
    '--cs-value-col': `${SHEET.valueCol}px`, '--cs-occurred-col': `${SHEET.occurredCol}px`, '--cs-ref-col': `${SHEET.refCol}px`, '--cs-stamps-col': `${SHEET.stampsCol}px`,
    '--cs-tools-col': `${SHEET.toolsCol}px`, '--cs-bar': `${SHEET.bar}px`, '--cs-bar-track': `${SHEET.barTrack}px`, '--cs-field': `${SHEET.field}px`, '--cs-dot': `${SHEET.dot}px`,
    '--cs-chip': `${SHEET.chip}px`,
    '--ca-prompt-col': `${ASSISTANT.promptCol}px`, '--ca-thread-max': `${ASSISTANT.threadMax}px`, '--ca-thread-min': `${ASSISTANT.threadMin}px`,
    '--ca-pick': `${ASSISTANT.pick}px`, '--ca-pick-wide': `${ASSISTANT.pickWide}px`, '--ca-question-max': ASSISTANT.questionShare,
    '--ca-marker': `${ASSISTANT.marker}px`, '--ca-feedback-box': `${ASSISTANT.feedbackBox}px`, '--ca-leading': ASSISTANT.leading,
  };
  Object.entries(p.bar).forEach(([name, c]) => { vars[`--cu-bar-${name}`] = c; });
  Object.entries(p.tones).forEach(([name, t]) => {
    vars[`--cu-${name}`] = t.fg; vars[`--cu-${name}-bg`] = t.bg; vars[`--cu-${name}-border`] = t.border;
  });
  return vars;
}
